/**
 * Join session page - enter code and name to join a game.
 */

import { useState, FormEvent, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SessionCodeInput } from '../components/medium/SessionCodeInput'
import { joinSession, ApiError } from '../services/api'
import { usePlayerStore } from '../stores/playerStore'
import { usePreferencesStore } from '../stores/preferencesStore'
import { useAuthStore } from '../stores/authStore'
import {
  isValidSessionCode,
  isValidPlayerName,
  getSessionCodeError,
  getPlayerNameError,
  normalizeSessionCode,
} from '../utils/validation'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { ConsentLabel } from '@/components/low/ConsentLabel'
import { BrandLogo } from '@/components/low/BrandLogo'

export function JoinPage() {
  const { t } = useTranslation()
  const { code: urlCode } = useParams<{ code?: string }>()
  const navigate = useNavigate()

  // Remembered nickname (device-level, rehydrated synchronously before first render)
  const lastNickname = usePreferencesStore((s) => s.lastNickname)
  const setLastNickname = usePreferencesStore((s) => s.setLastNickname)

  // Account name takes priority over the remembered one, but arrives asynchronously
  const authUser = useAuthStore((s) => s.user)
  const isAuthInitialized = useAuthStore((s) => s.isInitialized)

  // Form state
  const [code, setCode] = useState(urlCode ? normalizeSessionCode(urlCode) : '')
  const [typedName, setTypedName] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [touched, setTouched] = useState({ code: false, name: false })
  // Guests have no account, so the consent is not persisted — what matters legally
  // is that the documents were presented before joining.
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  // Store
  const { setPlayer, playerId, sessionCode } = usePlayerStore()

  // Prefill from account once initAuth() resolves, unless the player already typed.
  // Account usernames allow up to 50 chars while a player name allows 30, so an
  // over-long username is skipped to avoid a filled field with a blocked submit.
  // Derived instead of synced via effect, so no setState happens during an effect.
  const authUsername = authUser?.username ?? null
  const accountName =
    isAuthInitialized && authUsername !== null && isValidPlayerName(authUsername)
      ? authUsername
      : null
  const name = typedName ?? accountName ?? lastNickname ?? ''

  // Redirect only if:
  // 1. User came from QR/link with specific code (urlCode exists)
  // 2. User is already logged into THAT specific session
  // Never redirect if user clicked "Join" from main page (urlCode is undefined)
  useEffect(() => {
    if (urlCode && playerId && sessionCode) {
      // Check if user is already in the session they're trying to join
      const normalizedUrlCode = normalizeSessionCode(urlCode)
      if (sessionCode === normalizedUrlCode) {
        navigate(`/session/${sessionCode}/lobby`, { replace: true })
      }
      // If different session, let them join the new one (don't redirect)
    }
    // If no urlCode (came from main page "Join" button), never redirect
  }, [urlCode, playerId, sessionCode, navigate])

  // Validation
  const codeError = touched.code ? getSessionCodeError(code) : null
  const nameError = touched.name ? getPlayerNameError(name) : null
  const isValid = isValidSessionCode(code) && isValidPlayerName(name) && acceptedTerms

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    // Mark all as touched to show validation errors
    setTouched({ code: true, name: true })

    if (!isValid) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await joinSession(code, name)

      // Remember nickname for the next join on this device
      setLastNickname(result.name)

      // Store player data
      // Note: We use normalized session_code (not UUID) for URL routing consistency
      const sessionCode = normalizeSessionCode(code)
      setPlayer(
        result.player_id,
        result.name,
        sessionCode, // ABC-123-XYZ for URL routing
        null, // sessionUuid will be set when needed
        null,
        result.is_host,
        result.role,
        result.is_waiting
      )

      // Navigate to lobby after brief delay to allow store to sync
      // This prevents race condition where PhoneLobby checks credentials
      // before Zustand has updated the store
      setTimeout(() => {
        navigate(`/session/${sessionCode}/lobby`)
      }, 50)

    } catch (err) {
      const apiError = err as ApiError
      // AC5: Friendly message for CLOSED session
      const detail = apiError.detail ?? apiError.message ?? ''
      if (detail.toLowerCase().includes('closed')) {
        setError(t('join.sessionClosed'))
      } else if (detail.toLowerCase().includes('already taken')) {
        // Backend: "Player name '...' is already taken in this session" (409)
        setError(t('join.nameTaken'))
      } else if (detail.toLowerCase().includes('full') || apiError.status === 409) {
        // AC3: "Sesja pełna" — parse max_players from backend message "Session is full (max N players)"
        const match = detail.match(/max (\d+) players/)
        if (match) {
          setError(t('join.sessionFull', { count: parseInt(match[1], 10) }))
        } else {
          setError(t('join.sessionFullFallback'))
        }
      } else {
        setError(apiError.message ?? t('join.unknownError'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const isCodeFromUrl = !!urlCode

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-md mx-auto px-4 py-8 md:px-6 md:py-12">
        {/* Header */}
        <header className="mb-8">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 w-fit mb-6">
            <BrandLogo className="h-8 md:h-9" />
          </Link>
          <h1 className="text-xl font-display font-semibold text-foreground">{t('join.title')}</h1>
        </header>

        <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
          {/* Session code input */}
          <div>
            <Label
              htmlFor="session-code"
              className="block text-sm font-semibold uppercase tracking-[0.2em] text-foreground mb-3"
            >
              {t('join.sessionCodeLabel')}
            </Label>
            <SessionCodeInput
              value={code}
              onChange={(value) => {
                setCode(value)
                setTouched((t) => ({ ...t, code: true }))
              }}
              disabled={isCodeFromUrl || isLoading}
              error={codeError || undefined}
            />
            {isCodeFromUrl && (
              <p className="mt-2 text-xs text-muted-foreground uppercase tracking-widest">
                {t('join.loadedFromLink')}
              </p>
            )}
          </div>

          {/* Player name input */}
          <div>
            <Label
              htmlFor="player-name"
              className="block text-sm font-semibold uppercase tracking-[0.2em] text-foreground mb-3"
            >
              {t('join.playerNameLabel')}
            </Label>
            <Input
              id="player-name"
              type="text"
              value={name}
              onChange={(e) => {
                setTypedName(e.target.value)
                setTouched((t) => ({ ...t, name: true }))
              }}
              onBlur={() => setTouched((t) => ({ ...t, name: true }))}
              disabled={isLoading}
              placeholder={t('join.playerNamePlaceholder')}
              maxLength={30}
              autoComplete="off"
              className={`h-12 text-base ${nameError ? 'border-destructive' : ''}`}
              aria-invalid={!!nameError}
              aria-describedby={nameError ? 'name-error' : undefined}
            />
            {nameError && (
              <p id="name-error" className="mt-2 text-xs text-destructive uppercase tracking-widest">
                ❌ {nameError}
              </p>
            )}
          </div>

          {/* Terms and privacy consent */}
          <div className="flex items-start gap-2">
            <Checkbox
              id="join-accept-terms"
              data-testid="join-accept-terms"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              disabled={isLoading}
              className="mt-1"
            />
            <Label htmlFor="join-accept-terms" className="text-sm font-normal leading-snug">
              <ConsentLabel />
            </Label>
          </div>

          {/* General error message */}
          {error && (
            <Card className="p-4 border-destructive bg-destructive/10">
              <p className="text-destructive text-center text-sm uppercase tracking-widest font-bold">
                ❌ {error}
              </p>
            </Card>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 md:gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/')}
              disabled={isLoading}
              className="flex-1 min-h-[44px]"
              tabIndex={isLoading ? -1 : 0}
            >
              {t('join.back')}
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !isValid}
              className="flex-1 min-h-[44px] relative"
            >
              {isLoading ? (
                <>
                  <span className="opacity-0">{t('join.joinGame')}</span>
                  <span className="absolute inset-0 flex items-center justify-center">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  </span>
                </>
              ) : (
                `▶ ${t('join.joinGame')}`
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
